import { toast } from 'sonner';
import Dexie, { type Table } from 'dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { StudySetup } from './StudySetup';
import { clientApi } from '@/config/axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { uploadToS3 } from '@/features/uploads';
import { isCineFile, isStudyAttachment, queueUploadSet } from './uploadSet';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { apiError, type Study, useImagingApi, useImagingQuery } from './api';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface Pending {
  blob: Blob;
  clipId?: string;
  id?: number;
  name: string;
  scope: string;
  status: string;
  studyId?: string;
  targetStudyId?: string;
}
class Queue extends Dexie {
  files!: Table<Pending, number>;
  constructor() {
    super('mohair-imaging-upload-queue');
    this.version(1).stores({ files: '++id,scope' });
  }
}
const queue = new Queue();
export const isCine = isCineFile;
export function UploadIntake() {
  const { org, base, userId } = useImagingApi();
  const scope = `${org}:${userId}`;
  const rows = useLiveQuery(() => queue.files.where('scope').equals(scope).toArray(), [scope]);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const filesInput = useRef<HTMLInputElement>(null);
  const [target, setTarget] = useState('');
  const [preparing, setPreparing] = useState('');
  const [notice, setNotice] = useState('');
  const [dragging, setDragging] = useState(false);
  const [selectedStudy, setSelectedStudy] = useState<Study | null>(null);
  const [offset, setOffset] = useState(0);
  const [receiptOffset, setReceiptOffset] = useState(0);
  const serverCines = useImagingQuery<{ id: string; study_id: string; status: string; error?: string }[]>(
    `/cine-intake?offset=${receiptOffset}`,
  );
  const studies = useImagingQuery<Study[]>(`/intake?upload_targets=true&offset=${offset}`);
  const pendingCines = JSON.stringify(
    (rows || [])
      .filter((r) => r.clipId && !r.studyId)
      .map((r) => ({
        id: r.id,
        clipId: r.clipId,
        targetStudyId: r.targetStudyId,
      })),
  );
  useEffect(() => {
    let active = true;
    const timer = window.setInterval(async () => {
      for (const row of JSON.parse(pendingCines) as Pending[]) {
        if (!row.clipId || !row.targetStudyId) continue;
        try {
          const { data } = await clientApi.get(`${base}/studies/${row.targetStudyId}/clips/${row.clipId}/receipt`);
          if (!active) return;
          await queue.files.update(
            row.id!,
            data.status === 'ready'
              ? { blob: new Blob(), studyId: row.targetStudyId, status: 'Received · ready to play' }
              : { status: data.error || `Cine ${data.status}` },
          );
        } catch {
          /* Retain the local original; retry when connected. */
        }
      }
    }, 4000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [pendingCines, base]);
  async function add(files: FileList | null) {
    if (!files?.length) return;
    setBusy(true);
    setNotice('');
    setPreparing(`Reading ${files.length} selected ${files.length === 1 ? 'item' : 'items'}…`);
    try {
      const result = await queueUploadSet(
        Array.from(files),
        async (file) => {
          await queue.files.add({
            scope,
            blob: file,
            name: file.name,
            targetStudyId: target,
            status: target ? 'Waiting to upload' : 'Select a study before uploading',
          });
        },
        setPreparing,
      );
      setNotice(
        `${result.count} files ready to upload${result.skipped ? `; ${result.skipped} index/hidden entries skipped` : ''}. ${target ? 'Click Upload / retry files to begin.' : 'Select or create a study above, then click Upload / retry files.'}`,
      );
    } catch (e) {
      setNotice(
        `${e instanceof Error ? e.message : 'Unable to store files locally'}. Files already prepared remain in the queue. Review them before uploading.`,
      );
    } finally {
      setPreparing('');
      setBusy(false);
    }
  }
  async function submit() {
    if (rows?.some(r => !r.studyId && !r.targetStudyId) && !target) {
      setNotice('Your files are ready. Select or create a study above before uploading.');
      return;
    }
    setBusy(true);
    setNotice('');
    let failures = 0;
    try {
      for (const row of rows || []) {
        if (row.studyId) continue;
        if (!row.targetStudyId) {
          row.targetStudyId = target;
          await queue.files.update(row.id!, { targetStudyId: target });
        }
        await queue.files.update(row.id!, { status: 'Uploading' });
        try {
          if (isCine(row.name)) {
            if (!row.targetStudyId) throw Error('Choose the study for this cine clip before submitting');
            let result;
            if (row.clipId) {
              result = await clientApi.post(`${base}/studies/${row.targetStudyId}/clips/${row.clipId}/retry`);
            } else {
              const digest = await crypto.subtle.digest('SHA-256', await row.blob.arrayBuffer());
              const sha256 = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
              const file = new File([row.blob], row.name.split('/').pop()!, {
                type: row.blob.type || 'application/octet-stream',
              });
              const uploaded = await uploadToS3(org!, file, 'imaging-cine');
              result = await clientApi.post(`${base}/studies/${row.targetStudyId}/clips`, {
                sha256,
                object_name: uploaded.objectName,
              });
            }
            await queue.files.update(row.id!, {
              clipId: result.data.id,
              status: result.data.scheduled
                ? 'Cine processing · original retained locally'
                : 'Stored · retry to start processing',
            });
            continue;
          }
          const data = new FormData();
          data.append('file', row.blob, row.name);
          if (row.targetStudyId) data.append('target_study_id', row.targetStudyId);
          if (isStudyAttachment(row.name) && !row.targetStudyId) throw new Error("Select or create a study for JPG/PDF files.");
          const endpoint = isStudyAttachment(row.name) ? `${base}/studies/${row.targetStudyId}/attachments` : `${base}/objects`;
          const result = await clientApi.post(endpoint, data, { timeout: 300000 });
          await queue.files.update(row.id!, {
            blob: new Blob(),
            studyId: result.data.id,
            status: result.data.duplicate ? 'Already received' : 'Received',
          });
        } catch (e) {
          await queue.files.update(row.id!, {
            status: e instanceof Error && !(e as { response?: unknown }).response ? e.message : apiError(e),
          });
          failures++;
          continue;
        }
      }
      await qc.invalidateQueries({ queryKey: ['imaging', org] });
      setNotice(
        failures
          ? `${failures} files need attention. Other files were submitted; retry the failed files after resolving their errors.`
          : 'Files submitted. Cine clips may still be processing.',
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex flex-col gap-4">
      <StudySetup
        onSaved={(study) => {
          setTarget(study.id);
          setSelectedStudy(study);
        }}
      />
      <FieldGroup>
        <Field>
          <FieldLabel>Study for this imaging set</FieldLabel>
          <Select
            value={target}
            disabled={busy}
            onValueChange={(id) => {
              setTarget(id);
              setSelectedStudy(studies.data?.find((s) => s.id === id) || null);
            }}>
            <SelectTrigger>
              <SelectValue placeholder="Select a study or create one above" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {(selectedStudy && !studies.data?.some((s) => s.id === selectedStudy.id)
                  ? [selectedStudy, ...(studies.data || [])]
                  : studies.data || []
                )
                  .filter((s) => !s.signed_document_id)
                  .map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.metadata.PatientName || 'Unmatched study'} · {s.metadata.PatientID} ·{' '}
                      {s.description || s.metadata.StudyDescription || 'Imaging'} ·{' '}
                      {s.scheduled_at
                        ? new Date(s.scheduled_at).toLocaleString([], { timeZone: s.scheduled_timezone || 'UTC' })
                        : 'Unscheduled'}
                    </SelectItem>
                  ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <p>
            Today’s appointments appear first. Create a study for a new imaging set, including AVI-only sets. Confirm
            the selected patient and study before adding files. DICOM from a different study is rejected.
          </p>
          {selectedStudy && !selectedStudy.signed_document_id && (
            <StudySetup
              study={studies.data?.find((s) => s.id === target) || selectedStudy}
              onSaved={setSelectedStudy}
            />
          )}
          {studies.isError && <p role="alert">{apiError(studies.error)}</p>}
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={busy || offset === 0}
              onClick={() => setOffset((v) => Math.max(0, v - 100))}>
              Previous studies
            </Button>
            <Button
              variant="outline"
              disabled={busy || (studies.data?.length || 0) < 100}
              onClick={() => setOffset((v) => v + 100)}>
              Next studies
            </Button>
          </div>
        </Field>
        <Field>
          {!target && (
            <p id="imaging-study-required" role="status">
              Choose or drop your files below. Select or create a study above before uploading them.
            </p>
          )}
          <div
            className={`rounded-md border-2 border-dashed p-4 ${dragging ? 'border-primary bg-primary/10' : 'border-muted-foreground/40'}`}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
            }}
            onClick={(e) => {
              if (!busy && !(e.target as HTMLElement).closest('input, label, button')) filesInput.current?.click();
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              if (busy) return;
              if (!e.dataTransfer.files.length) {
                setNotice('Drop a ZIP or individual files here. To import a folder, use Select study folder below.');
                return;
              }
              void add(e.dataTransfer.files);
            }}>
            <p>Drop a ZIP file, DICOM, cine, JPG, or PDF files here, or choose files below.</p>
            <FieldLabel htmlFor="dicom-files">DICOM, JPG/JPEG, PDF, cine clips (AVI, MP4, MOV), or ZIP</FieldLabel>
            <Input
              id="dicom-files"
              ref={filesInput}
              multiple
              type="file"
              disabled={busy}
              aria-describedby={!target ? 'imaging-study-required' : undefined}
              onChange={(e) => {
                const input = e.currentTarget;
                void add(input.files).finally(() => {
                  input.value = '';
                });
              }}
            />
            <FieldLabel htmlFor="imaging-folder">Or select a complete study folder</FieldLabel>
            <Input
              id="imaging-folder"
              multiple
              type="file"
              {...{ directory: '', webkitdirectory: '' }}
              disabled={busy}
              onChange={(e) => {
                const input = e.currentTarget;
                void add(input.files).finally(() => {
                  input.value = '';
                });
              }}
            />
          </div>
        </Field>
      </FieldGroup>
      {preparing && <p role="status">{preparing}</p>}
      {notice && <p role="status">{notice}</p>}
      <Alert>
        <AlertDescription>
          Files are stored on this computer until received. Use a trusted device. Keep the original export until every
          object is confirmed received. ZIPs up to 2 GiB are unpacked automatically on this computer. Each selection
          supports up to 4 GiB expanded and 10,000 files; individual limits are 128 MiB per DICOM and 256 MiB per cine.
          Keep this page open while preparing and uploading. Received files are skipped on retry. Confirm every expected
          file is received before reviewing or signing the report.
        </AlertDescription>
      </Alert>
      <div className="flex gap-2">
        <Button disabled={busy || !rows?.some((r) => !r.studyId) || (!target && rows.some(r => !r.studyId && !r.targetStudyId))} onClick={() => void submit()}>
          {busy ? 'Submitting…' : 'Upload / retry files'}
        </Button>
        <Button
          variant="outline"
          disabled={busy || !target || !rows?.some((r) => !r.studyId && !r.clipId)}
          onClick={async () => {
            if (
              !window.confirm(
                'Assign all waiting files to the selected patient and study? Verify the source images belong to this patient.',
              )
            )
              return;
            await queue.files
              .where('scope')
              .equals(scope)
              .filter((r) => !r.studyId && !r.clipId)
              .modify({ targetStudyId: target, status: 'Waiting to upload' });
          }}>
          Assign waiting files to selected study
        </Button>
        <Button
          disabled={busy}
          variant="outline"
          onClick={() =>
            void queue.files
              .where('scope')
              .equals(scope)
              .filter((r) => !!r.studyId)
              .delete()
          }>
          Clear receipts
        </Button>
      </div>
      <details>
        <summary>Server cine submissions awaiting completion</summary>
        <p>These receipts remain available after closing or changing computers.</p>
        <Button
          disabled={busy}
          variant="outline"
          onClick={() => void qc.invalidateQueries({ queryKey: ['imaging', org] })}>
          Refresh cine receipts
        </Button>
        {serverCines.data?.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center gap-2">
            <span>
              Study {c.study_id} · {c.error || c.status}
            </span>
            <Button
              disabled={busy}
              variant="outline"
              onClick={async () => {
                setBusy(true);
                try {
                  const { data } = await clientApi.post(`${base}/studies/${c.study_id}/clips/${c.id}/retry`);
                  toast.info(
                    data.scheduled
                      ? 'Cine processing requested.'
                      : 'Processing service unavailable. Retry when it is restored.',
                  );
                  await qc.invalidateQueries({ queryKey: ['imaging', org] });
                } catch (e) {
                  toast.error(apiError(e));
                } finally {
                  setBusy(false);
                }
              }}>
              Retry processing
            </Button>
          </div>
        ))}
        {serverCines.isError && <p role="alert">{apiError(serverCines.error)}</p>}
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={busy || receiptOffset === 0}
            onClick={() => setReceiptOffset((v) => Math.max(0, v - 100))}>
            Previous receipts
          </Button>
          <Button
            variant="outline"
            disabled={busy || (serverCines.data?.length || 0) < 100}
            onClick={() => setReceiptOffset((v) => v + 100)}>
            Next receipts
          </Button>
        </div>
      </details>
      {rows?.map((r) => (
        <div key={r.id} className="flex items-center justify-between gap-3">
          <span>
            {r.name} · {r.status}
          </span>
          {isCine(r.name) && !r.clipId && !r.studyId && (
            <Button
              variant="outline"
              disabled={busy || !target}
              onClick={() => void queue.files.update(r.id!, { targetStudyId: target })}>
              {r.targetStudyId ? 'Change to selected study' : 'Use selected study'}
            </Button>
          )}
          {isCine(r.name) && r.targetStudyId && <span>Study: {r.targetStudyId}</span>}
          <Button
            disabled={busy}
            variant="ghost"
            onClick={() => {
              if (
                window.confirm('Remove this local queue entry? Keep your original export if it has not been received.')
              )
                void queue.files.delete(r.id!);
            }}>
            Remove
          </Button>
        </div>
      ))}
    </div>
  );
}
